import { Text } from '~/ui/index';
import Modal from '~/ui/components/modal/index';
import React from 'react';
import { useMutation } from '@tanstack/react-query';
import { dazzler } from '~/data/dazzler-api';
import type {
  CreateValidationChecksFileValidator,
  ValidationChecksComparisonValidator,
} from '~/data/openapi-client/index';
import { Button, CircularProgress, Divider, TextField } from '@mui/material';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useListClients } from '~/data/clients/queries';
import { useGetReports } from '~/data/reports/queries';
import { formatDate } from '~/app/utils/format-data';
import { assert } from '@std/assert';

export type CreateComparisonModal = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

const Values = z.object({
  leftClient: z.string().optional(),
  leftReport: z.string().optional(),
  leftFile: z.string().optional(),
  rightClient: z.string().optional(),
  rightReport: z.string().optional(),
  rightFile: z.string().optional(),
});

type Values = z.infer<typeof Values>;

export function CreateComparisonModal({ open, onClose, onSuccess }: CreateComparisonModal) {
  const { mutateAsync: createComparison } = useMutation({
    mutationFn: async function createComparison(body: ValidationChecksComparisonValidator) {
      await dazzler.validationChecksComparisons.createComparison({ validationChecksComparisonValidator: body });
    },
    onSuccess: onSuccess,
  });

  const { mutateAsync: uploadFile, isLoading: isFileUploading } = useMutation({
    mutationFn: async function uploadFile(file: File) {
      const response = await dazzler.validationChecksComparisons.uploadComparisonFile({
        file,
      });

      return response.data;
    },
  });

  const { mutateAsync: createFile } = useMutation({
    mutationFn: async function createFile(body: CreateValidationChecksFileValidator) {
      const response = await dazzler.validationChecksComparisons.createComparisonFile({
        createValidationChecksFileValidator: body,
      });
      return response.data;
    },
  });

  const { data: clientList } = useListClients({
    limit: 100,
  });

  const { register, setValue, resetField, handleSubmit, watch } = useForm<Values>({
    resolver: zodResolver(Values),
  });

  const onSubmit = handleSubmit(async function submitComparison(data) {
    let leftFile = data.leftFile;
    let rightFile = data.rightFile;

    if (!leftFile) {
      assert(data.leftClient && data.leftReport, 'the left client and report must be present');
      const f = await createFile({
        clientId: data.leftClient,
        reportId: data.leftReport,
      });
      leftFile = f.id;
    }

    if (!rightFile) {
      assert(data.rightClient && data.rightReport, 'the right client and report must be present');
      const f = await createFile({
        clientId: data.rightClient,
        reportId: data.rightReport,
      });
      rightFile = f.id;
    }

    await createComparison({
      leftComparisonFileId: leftFile,
      rightComparisonFileId: rightFile,
    });
  });

  const leftClient = watch('leftClient');
  const rightClient = watch('rightClient');

  const { data: leftClientReports } = useGetReports(
    leftClient!,
    {
      limit: 100,
    },
    {
      enabled: !!leftClient,
    },
  );

  const { data: rightClientReports } = useGetReports(
    rightClient!,
    {
      limit: 100,
    },
    {
      enabled: !!rightClient,
    },
  );

  const onFileUpload: React.ChangeEventHandler<HTMLInputElement> = async (event) => {
    const files = event.currentTarget.files;
    if (!files) return;
    const file = files.item(0);
    if (!file) {
      return;
    }

    const name = z.enum(['leftFile', 'rightFile']).parse(event.currentTarget.name);
    const f = await uploadFile(file);
    setValue(name, f.id);
    if (name == 'leftFile') {
      resetField('leftReport');
    } else {
      resetField('rightReport');
    }
  };

  return (
    <Modal open={open} onClose={onClose} modalTitle={<Text className="font-semibold">Create new comparison</Text>}>
      <form onSubmit={onSubmit}>
        <div className="mb-5">
          <div className="mb-3">
            <Text className="text-base mb-3">Left</Text>
            <div className="flex flex-col gap-y-2">
              <TextField
                {...register('leftClient')}
                select
                label="Client"
                InputLabelProps={{
                  shrink: true,
                }}
                SelectProps={{
                  native: true,
                }}
              >
                <option value="">None</option>
                {clientList?.items.map((client) => (
                  <option key={client.id} value={client.id!}>
                    {client.name}
                  </option>
                ))}
              </TextField>

              <TextField
                {...register('leftReport')}
                select
                label="Report"
                InputLabelProps={{
                  shrink: true,
                }}
                SelectProps={{
                  native: true,
                }}
              >
                <option value="">None</option>
                {leftClientReports?.items.map((report) => (
                  <option key={report.id} value={report.id!}>
                    {report.name} ({formatDate(report.inserted)})
                  </option>
                ))}
              </TextField>

              <Divider>OR</Divider>

              <TextField
                type="file"
                name="leftFile"
                onChange={onFileUpload}
                InputProps={{
                  endAdornment: isFileUploading ? <CircularProgress size={'1em'} /> : undefined,
                }}
              />
            </div>
          </div>
          <div>
            <Text className="text-base mb-3">Right</Text>
            <div className="flex flex-col gap-y-2">
              <TextField
                {...register('rightClient')}
                select
                label="Client"
                InputLabelProps={{
                  shrink: true,
                }}
                SelectProps={{
                  native: true,
                }}
              >
                <option value="">None</option>
                {clientList?.items.map((client) => (
                  <option key={client.id} value={client.id!}>
                    {client.name}
                  </option>
                ))}
              </TextField>

              <TextField
                {...register('rightReport')}
                select
                label="Report"
                InputLabelProps={{
                  shrink: true,
                }}
                SelectProps={{
                  native: true,
                }}
              >
                <option value="">None</option>
                {rightClientReports?.items.map((report) => (
                  <option key={report.id} value={report.id!}>
                    {report.name} ({formatDate(report.inserted)})
                  </option>
                ))}
              </TextField>

              <Divider>OR</Divider>

              <TextField
                type="file"
                name="rightFile"
                onChange={onFileUpload}
                InputProps={{
                  endAdornment: isFileUploading ? <CircularProgress size={'1em'} /> : undefined,
                }}
              />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-x-2">
          <Button type="button" variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="contained">
            Submit
          </Button>
        </div>
      </form>
    </Modal>
  );
}
